// Plan Semanal IA — Lote F7.
// Genera plan personalizado con Gemini basado en la cartera del comercial.
// Dir_comercial/admin: puede descargar Informe Semanal PDF agregado del equipo.
import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Sparkles, Loader2, Download, CheckCircle2, XCircle, Trophy,
  Calendar, Phone, Mail, Users, FileText, RefreshCw, Target, Zap, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { planSemanalApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Switch } from '../components/ui/switch';

const DIAS = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes'];

const TIPO_ICON = {
  visita: <Users className="w-3.5 h-3.5" />,
  llamada: <Phone className="w-3.5 h-3.5" />,
  email: <Mail className="w-3.5 h-3.5" />,
  oferta: <FileText className="w-3.5 h-3.5" />,
  seguimiento: <Target className="w-3.5 h-3.5" />,
  reunion: <Calendar className="w-3.5 h-3.5" />,
};

const PRIO_COLOR = {
  alta: 'bg-red-50 border-red-200 text-red-700',
  media: 'bg-amber-50 border-amber-200 text-amber-700',
  baja: 'bg-emerald-50 border-emerald-200 text-emerald-700',
};

const PropuestaRow = ({ prop, onEstado }) => {
  const estado = prop.completada ? 'completada' : prop.aceptada ? 'aceptada' : prop.rechazada ? 'rechazada' : null;
  return (
    <div
      className={`px-3 py-2 border rounded-lg flex items-start gap-2 ${estado === 'completada' ? 'bg-emerald-50/60 border-emerald-200' : estado === 'rechazada' ? 'bg-ink-50 border-ink-200 opacity-60' : 'bg-white border-ink-200'}`}
      data-testid={`plan-propuesta-${prop.id}`}
    >
      <div className="flex items-center gap-1 shrink-0 mt-0.5">
        {TIPO_ICON[prop.tipo] || <Target className="w-3.5 h-3.5" />}
        <Badge className={`${PRIO_COLOR[prop.prioridad] || 'bg-ink-50 border-ink-200 text-ink-600'} text-[9px] border`}>{prop.prioridad}</Badge>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-ink-800">
          {prop.accion_concreta}
          {estado === 'aceptada' && <Badge className="ml-2 bg-blue-50 border-blue-200 text-blue-700 border text-[9px]">Aceptada</Badge>}
          {estado === 'completada' && <Badge className="ml-2 bg-emerald-50 border-emerald-200 text-emerald-700 border text-[9px]">✔ Completada</Badge>}
          {estado === 'rechazada' && <Badge className="ml-2 bg-ink-50 border-ink-200 text-ink-500 border text-[9px]">Rechazada</Badge>}
        </div>
        <div className="text-xs text-ink-500 mt-0.5">
          {prop.entidad_nombre || '—'} · {prop.canal || '—'} · {prop.duracion_min || 30}min
        </div>
        {prop.motivo && <div className="text-xs text-ink-600 mt-1 italic">{prop.motivo}</div>}
      </div>
      {!estado && (
        <div className="flex items-center gap-1 shrink-0">
          <Button
            size="sm" variant="ghost"
            className="h-7 px-2 text-blue-700 hover:bg-blue-50"
            data-testid={`plan-aceptar-${prop.id}`}
            aria-label="Aceptar propuesta"
            onClick={() => onEstado(prop.id, 'aceptada')}
          >
            <CheckCircle2 className="w-4 h-4" />
          </Button>
          <Button
            size="sm" variant="ghost"
            className="h-7 px-2 text-ink-500 hover:bg-ink-50"
            data-testid={`plan-rechazar-${prop.id}`}
            aria-label="Rechazar propuesta"
            onClick={() => onEstado(prop.id, 'rechazada')}
          >
            <XCircle className="w-4 h-4" />
          </Button>
        </div>
      )}
      {estado === 'aceptada' && (
        <Button
          size="sm" variant="ghost"
          className="h-7 px-2 text-emerald-700 hover:bg-emerald-50"
          data-testid={`plan-completar-${prop.id}`}
          aria-label="Marcar como completada"
          onClick={() => onEstado(prop.id, 'completada')}
        >
          <Trophy className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};

export default function PlanSemanal() {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [proactivo, setProactivo] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [smtpOk, setSmtpOk] = useState(false);
  const [savingProactivo, setSavingProactivo] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [enviandoEmail, setEnviandoEmail] = useState(false);

  useEffect(() => {
    cargarActual();
    (async () => {
      try {
        const cfg = await planSemanalApi.getConfig();
        setProactivo(!!cfg?.proactivo);
        setEmailEnabled(!!cfg?.email);
        setSmtpOk(!!cfg?.smtp_configurado);
      } catch { /* silencioso */ }
    })();
  }, []);

  const togglePoractivo = async (v) => {
    setSavingProactivo(true);
    const prev = proactivo;
    setProactivo(v);
    try {
      await planSemanalApi.setConfig({ proactivo: v });
      toast.success(v ? 'Plan automático activado: cada lunes a las 7:00' : 'Plan automático desactivado');
    } catch (e) {
      setProactivo(prev);
      toast.error(e.message);
    } finally { setSavingProactivo(false); }
  };

  const toggleEmail = async (v) => {
    setSavingEmail(true);
    const prev = emailEnabled;
    setEmailEnabled(v);
    try {
      await planSemanalApi.setConfig({ email: v });
      toast.success(v ? 'Recibirás el plan por email cada lunes' : 'Email semanal desactivado');
    } catch (e) {
      setEmailEnabled(prev);
      toast.error(e.message);
    } finally { setSavingEmail(false); }
  };

  const enviarPorEmail = async () => {
    setEnviandoEmail(true);
    try {
      const r = await planSemanalApi.enviarEmail();
      toast.success(`Plan enviado a ${r.destinatario}`);
    } catch (e) {
      toast.error(e.message);
    } finally { setEnviandoEmail(false); }
  };

  const cargarActual = async () => {
    setLoading(true);
    try {
      const data = await planSemanalApi.actual();
      if (data && data.existe !== false) setPlan(data);
      else setPlan(null);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const generar = async (forzar = false) => {
    setGenerando(true);
    try {
      const p = await planSemanalApi.generar(forzar);
      setPlan(p);
      toast.success(forzar ? 'Plan regenerado' : `Plan semanal: ${p.propuestas?.length || 0} propuestas`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGenerando(false);
    }
  };

  const cambiarEstado = async (propId, estado) => {
    // Optimistic update con backup para rollback
    const prev = plan;
    setPlan((p) => ({
      ...p,
      propuestas: p.propuestas.map((x) =>
        x.id === propId
          ? { ...x, aceptada: estado === 'aceptada', rechazada: estado === 'rechazada', completada: estado === 'completada' }
          : x),
    }));
    try {
      await planSemanalApi.cambiarEstado(plan.id, propId, estado);
    } catch (e) {
      setPlan(prev);
      toast.error(e.message);
    }
  };

  const descargarPdf = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(planSemanalApi.informePdfUrl(), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Error generando PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `informe_semanal_${plan?.semana_iso || 'actual'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const puedeVerEquipo = user && ['admin', 'dir_comercial', 'admin_comercial'].includes(user.rol);

  const propuestasPorDia = DIAS.map((d) => ({
    dia: d,
    props: (plan?.propuestas || []).filter((p) => (p.dia_sugerido || '').toLowerCase() === d),
  }));
  const sinDia = (plan?.propuestas || []).filter((p) => !DIAS.includes((p.dia_sugerido || '').toLowerCase()));

  const kpis = plan ? {
    total: (plan.propuestas || []).length,
    aceptadas: (plan.propuestas || []).filter((p) => p.aceptada || p.completada).length,
    completadas: (plan.propuestas || []).filter((p) => p.completada).length,
    rechazadas: (plan.propuestas || []).filter((p) => p.rechazada).length,
  } : null;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5" data-testid="plan-semanal-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-ink-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-violet-600" /> Plan Semanal IA
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Gemini analiza tu cartera, tus visitas, tus ofertas abiertas y te propone las acciones comerciales de la semana.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-violet-200 bg-violet-50/50"
            data-testid="plan-toggle-proactivo-wrap"
            title="Si lo activas, Gemini te genera el plan cada lunes a las 7:00 sin tener que pulsar Generar"
          >
            <Zap className="w-4 h-4 text-violet-600" />
            <span className="text-xs text-violet-900 font-medium">Generar cada lunes</span>
            <Switch
              checked={proactivo}
              disabled={savingProactivo}
              onCheckedChange={togglePoractivo}
              data-testid="plan-toggle-proactivo"
            />
          </div>
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${smtpOk ? 'border-blue-200 bg-blue-50/50' : 'border-ink-200 bg-ink-50 opacity-60'}`}
            data-testid="plan-toggle-email-wrap"
            title={smtpOk
              ? 'Recibirás el plan en tu email cada lunes (junto a la generación automática).'
              : 'SMTP no configurado en el servidor. Pide al admin que rellene SMTP_HOST/USER/PASSWORD en .env.'}
          >
            <Mail className={`w-4 h-4 ${smtpOk ? 'text-blue-600' : 'text-ink-400'}`} />
            <span className={`text-xs font-medium ${smtpOk ? 'text-blue-900' : 'text-ink-500'}`}>
              Recibir por email
            </span>
            <Switch
              checked={emailEnabled}
              disabled={savingEmail || !smtpOk}
              onCheckedChange={toggleEmail}
              data-testid="plan-toggle-email"
            />
          </div>
          {plan && smtpOk && (
            <Button
              onClick={enviarPorEmail}
              disabled={enviandoEmail}
              variant="outline"
              data-testid="btn-enviar-email-plan"
              title="Enviar el plan ahora a tu email"
            >
              {enviandoEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar email
            </Button>
          )}
          {puedeVerEquipo && plan && (
            <Button
              onClick={descargarPdf}
              variant="outline"
              data-testid="btn-descargar-pdf-informe"
            >
              <Download className="w-4 h-4 mr-2" /> Informe PDF equipo
            </Button>
          )}
          <Button
            onClick={() => generar(!!plan)}
            disabled={generando}
            data-testid="btn-generar-plan"
            className="bg-violet-600 hover:bg-violet-700"
          >
            {generando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : plan ? <RefreshCw className="w-4 h-4 mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {plan ? 'Regenerar plan' : 'Generar plan semanal'}
          </Button>
        </div>
      </div>

      {loading && <div className="flex items-center gap-2 text-ink-500 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Cargando plan…</div>}

      {!loading && !plan && (
        <Card className="p-8 text-center border-dashed">
          <Sparkles className="w-10 h-10 mx-auto text-violet-400 mb-3" />
          <h3 className="text-lg font-semibold text-ink-800">Aún no tienes plan para esta semana</h3>
          <p className="text-sm text-ink-500 mt-1">
            Pulsa <b>Generar plan semanal</b> y la IA te propondrá las acciones prioritarias de tu cartera.
          </p>
        </Card>
      )}

      {plan && (
        <>
          <Card className="p-4 bg-gradient-to-br from-violet-50/60 to-white border-violet-200">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <Badge className="bg-violet-100 border-violet-300 text-violet-800 border text-xs">
                  Semana {plan.semana_iso}
                </Badge>
                <div className="text-xs text-ink-500 mt-1">
                  {plan.fecha_inicio} → {plan.fecha_fin} · Generado para {plan.user_nombre || plan.user_email}
                </div>
              </div>
              {kpis && (
                <div className="flex gap-4 text-center">
                  <div><div className="text-xl font-bold text-ink-900">{kpis.total}</div><div className="text-[10px] text-ink-500">Propuestas</div></div>
                  <div><div className="text-xl font-bold text-blue-700">{kpis.aceptadas}</div><div className="text-[10px] text-ink-500">Aceptadas</div></div>
                  <div><div className="text-xl font-bold text-emerald-700">{kpis.completadas}</div><div className="text-[10px] text-ink-500">✔ Hechas</div></div>
                </div>
              )}
            </div>
            {plan.fallback_usado && (
              <div
                className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex items-start gap-2"
                data-testid="plan-fallback-notice"
              >
                <span className="font-semibold shrink-0">⚠ Plan de respaldo:</span>
                <span>la IA no pudo procesar tu cartera en este momento. Te dejamos una agenda derivada directamente de tus visitas, ofertas y LEADs. Puedes pulsar <b>Regenerar plan</b> en unos minutos.</span>
              </div>
            )}
            {plan.resumen_semana && (
              <div className="mt-3 p-3 bg-white rounded-lg border border-violet-100 text-sm text-ink-700 italic">
                {plan.resumen_semana}
              </div>
            )}
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {propuestasPorDia.map(({ dia, props }) => (
              <Card key={dia} className="p-3 min-h-[140px]" data-testid={`plan-dia-${dia}`}>
                <div className="font-semibold text-sm text-ink-700 mb-2 capitalize flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5 text-violet-500" /> {dia}
                  <span className="ml-auto text-[10px] text-ink-400">{props.length}</span>
                </div>
                <div className="space-y-2">
                  {props.length === 0 && <div className="text-xs text-ink-400 italic">Sin acciones</div>}
                  {props.map((p) => <PropuestaRow key={p.id} prop={p} onEstado={cambiarEstado} />)}
                </div>
              </Card>
            ))}
          </div>

          {sinDia.length > 0 && (
            <Card className="p-3">
              <div className="font-semibold text-sm text-ink-700 mb-2">Sin día asignado ({sinDia.length})</div>
              <div className="space-y-2">
                {sinDia.map((p) => <PropuestaRow key={p.id} prop={p} onEstado={cambiarEstado} />)}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
